const euro = (cents) => new Intl.NumberFormat('nl-NL', {
  style: 'currency',
  currency: 'EUR',
  minimumFractionDigits: Number(cents) % 100 === 0 ? 0 : 2,
  maximumFractionDigits: 2
}).format(Number(cents) / 100);

const initializePriceConfigurator = () => {
  document.querySelectorAll('[data-price-configurator]').forEach((root) => {
    const configuration = JSON.parse(root.querySelector('[data-price-configuration]').textContent);
    const fields = Object.fromEntries(Array.from(root.querySelectorAll('[data-price-field]')).map((field) => [field.dataset.priceField, field]));
    const error = root.querySelector('[data-price-error]');
    let requestNumber = 0;

    const customLines = (type) => Array.from(root.querySelector(`[data-custom-lines="${type}"]`).querySelectorAll('[data-custom-line]')).map((line) => ({
      description: line.querySelector('[data-custom-description]').value,
      amount_euros: line.querySelector('[data-custom-amount]').value
    }));

    const selectedConfiguration = () => configuration[fields.service_id.value];

    const currentPayload = () => {
      const payload = Object.fromEntries(Object.entries(fields).map(([key, field]) => [key, field.value]));
      payload.custom_license_lines = customLines('license');
      payload.custom_implementation_lines = customLines('implementation');
      return payload;
    };

    const populateProductOptions = () => {
      const config = selectedConfiguration();
      fields.organisation_type_id.innerHTML = config.organisation_types.map((type) => `<option value="${type.id}">${type.name}</option>`).join('');
      fields.duration_months.innerHTML = config.contract_terms.map((term) => `<option value="${term.duration_months}">${term.label}${Number(term.discount_percentage) ? ` · ${term.discount_percentage}% korting` : ''}</option>`).join('');
      fields.implementation_hours.value = Number(config.implementation_hours);
      updateOrganisationDescription();
    };

    const updateOrganisationDescription = () => {
      const selected = selectedConfiguration().organisation_types.find((type) => type.id === fields.organisation_type_id.value);
      root.querySelector('[data-organisation-description]').textContent = selected?.description || '';
    };

    const render = (result) => {
      root.querySelector('[data-result-product]').textContent = result.product;
      ['implementation_amount', 'technical_setup_amount', 'one_time_amount'].forEach((key) => {
        root.querySelector(`[data-result="${key}"]`).textContent = euro(result[key]);
      });
      ['base_amount', 'account_surcharge'].forEach((key) => {
        root.querySelector(`[data-result="${key}"]`).textContent = euro(Number(result[key]) / 12);
      });
      root.querySelector('[data-result="contract_discount"]').textContent = result.contract_discount ? `− ${euro(Number(result.contract_discount) / 12)}` : euro(0);
      root.querySelector('[data-result="annual_amount"]').textContent = euro(result.annual_amount);
      root.querySelector('[data-result="monthly_amount"]').textContent = euro(result.monthly_amount);
      root.querySelector('[data-result="monthly_breakdown_total"]').textContent = euro(result.monthly_amount);
      root.querySelector('[data-result-meta="organisation_type"]').textContent = result.organisation_type;
      root.querySelector('[data-result-meta="account_tier"]').textContent = `${result.account_tier} · ${result.account_percentage}%`;
      root.querySelector('[data-result-meta="contract_label"]').textContent = `${result.contract_label} · ${result.discount_percentage}%`;
      root.querySelector('[data-result-meta="implementation"]').textContent = `${result.implementation_hours} uur × ${euro(result.hourly_rate)}`;
      root.querySelector('[data-result-meta="technical_setup"]').textContent = `${result.technical_setup_percentage}% van basisprijs + accountopslag, vóór contractkorting`;

      [['license', result.custom_license_lines], ['implementation', result.custom_implementation_lines]].forEach(([type, lines]) => {
        const container = root.querySelector(`[data-result-custom-lines="${type}"]`);
        container.replaceChildren(...lines.map((line) => {
          const row = document.createElement('div');
          const label = document.createElement('span');
          const amount = document.createElement('strong');
          label.textContent = line.description;
          amount.textContent = euro(type === 'license' ? Number(line.amount) / 12 : line.amount);
          row.append(label, amount);
          return row;
        }));
        container.hidden = lines.length === 0;
      });
    };

    const calculate = async () => {
      const currentRequest = ++requestNumber;
      const payload = currentPayload();
      try {
        const response = await fetch(root.dataset.calculateUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content,
            'Accept': 'application/json'
          },
          body: JSON.stringify(payload)
        });
        const result = await response.json();
        if (currentRequest !== requestNumber) return;
        if (!response.ok) throw new Error(result.error);
        error.hidden = true;
        render(result);
      } catch (exception) {
        if (currentRequest !== requestNumber) return;
        error.textContent = exception.message || 'De prijs kon niet worden berekend.';
        error.hidden = false;
      }
    };

    let debounce;
    root.addEventListener('click', (event) => {
      const addButton = event.target.closest('[data-add-custom-line]');
      if (addButton) {
        const template = root.querySelector('[data-custom-line-template]');
        root.querySelector(`[data-custom-lines="${addButton.dataset.addCustomLine}"]`).append(template.content.cloneNode(true));
        return;
      }

      const removeButton = event.target.closest('[data-remove-custom-line]');
      if (removeButton) {
        removeButton.closest('[data-custom-line]').remove();
        calculate();
        return;
      }

      const exportButton = event.target.closest('[data-export-price]');
      if (exportButton) {
        const label = exportButton.querySelector('span');
        const originalText = label.textContent;
        exportButton.disabled = true;
        label.textContent = 'PDF maken…';
        fetch(root.dataset.exportUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content,
            'Accept': 'application/pdf'
          },
          body: JSON.stringify(currentPayload())
        }).then(async (response) => {
          if (!response.ok) {
            const body = await response.json();
            throw new Error(body.error);
          }
          const blob = await response.blob();
          const downloadUrl = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = downloadUrl;
          link.download = `prijsconfiguratie-${fields.service_id.options[fields.service_id.selectedIndex].text.toLowerCase().replace(/[^a-z0-9]+/g, '-')}.pdf`;
          link.click();
          URL.revokeObjectURL(downloadUrl);
        }).catch((exception) => {
          error.textContent = exception.message || 'De PDF kon niet worden gemaakt.';
          error.hidden = false;
        }).finally(() => {
          exportButton.disabled = false;
          label.textContent = originalText;
        });
      }
    });
    root.addEventListener('input', () => {
      clearTimeout(debounce);
      debounce = setTimeout(calculate, 180);
    });
    fields.service_id.addEventListener('change', () => { populateProductOptions(); calculate(); });
    fields.organisation_type_id.addEventListener('change', () => { updateOrganisationDescription(); calculate(); });
    root.addEventListener('change', (event) => {
      if (event.target !== fields.service_id && event.target !== fields.organisation_type_id) calculate();
    });

    populateProductOptions();
    calculate();
  });
};

document.addEventListener('DOMContentLoaded', initializePriceConfigurator);
